import type { FieldErrors } from 'react-hook-form';
import type { User } from '@/types';
import type { UserFieldAccess } from '@/types/user.types';
import type { EditUserFormValues } from '../../schemas/editUser.schemas';
import { text } from './profileFormat';

export type ProfileTab = 'overview' | 'employment' | 'pay' | 'contact' | 'documents';

type Bag = Record<string, unknown>;

/** What the API let this viewer see; older responses without the flag show everything. */
export function fieldAccessOf(user: Pick<User, 'fieldAccess'>): UserFieldAccess {
  return user.fieldAccess ?? { sensitive: true, payRates: true };
}

export function bags(user: User) {
  const personalInfo = (user.personalInfo ?? {}) as Bag;
  const contactInfo = (user.contactInfo ?? {}) as Bag;
  const compensation = (user.compensation ?? {}) as Bag;
  return {
    personalInfo,
    emergency: (personalInfo.emergencyContact ?? {}) as Bag,
    contactInfo,
    address: (contactInfo.address ?? {}) as Bag,
    employment: (user.employment ?? {}) as Bag,
    compensation,
    bank: (compensation.bankDetails ?? {}) as Bag,
    tax: (compensation.taxInfo ?? {}) as Bag,
  };
}

/** A field HR expects on a complete profile, and the tab where it's edited. */
export interface ProfileGap {
  label: string;
  tab: Exclude<ProfileTab, 'documents'>;
}

/** The fields that make a profile usable for payroll and HR, in display order. */
export function profileGaps(user: User): { total: number; missing: ProfileGap[] } {
  const b = bags(user);
  const access = fieldAccessOf(user);
  // Hidden fields can't be judged missing, so they don't count either way.
  const all: Array<[string, ProfileGap['tab'], unknown, boolean?]> = [
    ['Date of birth', 'overview', b.personalInfo.dateOfBirth, access.sensitive],
    ['Gender', 'overview', b.personalInfo.gender],
    ['Nationality', 'overview', b.personalInfo.nationality],
    ['CNIC number', 'overview', b.personalInfo.cnicNumber, access.sensitive],
    ['Department', 'employment', b.employment.department],
    ['Designation', 'employment', b.employment.designation],
    ['Employment type', 'employment', b.employment.employmentType],
    ['Joining date', 'employment', b.employment.joiningDate],
    ['Pay rate', 'pay', b.compensation.hourlyRate ?? b.compensation.baseSalary, access.payRates],
    ['Bank account', 'pay', b.bank.accountNumber, access.sensitive],
    ['Phone', 'contact', b.contactInfo.phone],
    ['Address', 'contact', b.address.city ?? b.address.street],
    ['Emergency contact', 'contact', b.emergency.phone ?? b.emergency.name],
  ];
  const checks = all.filter(([, , , visible]) => visible !== false);
  return {
    total: checks.length,
    missing: checks
      .filter(([, , value]) => text(value) == null)
      .map(([label, tab]) => ({ label, tab })),
  };
}

/** Which tab owns each top-level form branch, for flagging tabs that hold errors. */
export function tabsWithErrors(errors: FieldErrors<EditUserFormValues>): Set<ProfileTab> {
  const tabs = new Set<ProfileTab>();
  if (errors.firstName || errors.lastName || errors.email || errors.avatar) tabs.add('overview');
  if (errors.personalInfo) {
    const { emergencyContact, ...personal } = errors.personalInfo;
    if (Object.keys(personal).length > 0) tabs.add('overview');
    if (emergencyContact) tabs.add('contact');
  }
  if (errors.contactInfo) tabs.add('contact');
  if (errors.employment) tabs.add('employment');
  if (errors.compensation) tabs.add('pay');
  return tabs;
}

const TAB_ORDER: ProfileTab[] = ['overview', 'employment', 'pay', 'contact'];

export function firstTabWithError(errors: FieldErrors<EditUserFormValues>): ProfileTab | null {
  const tabs = tabsWithErrors(errors);
  return TAB_ORDER.find((tab) => tabs.has(tab)) ?? null;
}
